import { useCallback, useEffect, useState } from 'react';
import { AppLogo } from '@/components/layout/AppLogo';
import { AlertCircle, Mail, RefreshCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/config/routes';
import {
  onboardingService,
  type AccessStatusResponse,
} from '@/services/onboardingService';
import { CyberBackground } from '@/components/landing/CyberBackground';

const SUPPORT_EMAIL = 'support@assetmanagement.io';

export default function RejectedOrganizationPage() {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const [status, setStatus] = useState<AccessStatusResponse | null>(null);

  const refreshStatus = useCallback(async () => {
    try {
      const nextStatus = await onboardingService.getAccessStatus();
      setStatus(nextStatus);

      if (nextStatus.state === 'approved' && !nextStatus.requiresReauthentication) {
        navigate(ROUTES.HOME, { replace: true });
        return;
      }

      if (nextStatus.state === 'pending_approval') {
        navigate(ROUTES.WAITING_APPROVAL, { replace: true });
        return;
      }

      if (nextStatus.state === 'no_organization') {
        navigate(ROUTES.NO_ORGANIZATION, { replace: true });
      }
    } catch {
      // Keep the rejection page visible if status refresh is temporarily unavailable.
    }
  }, [navigate]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  const emailHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Organization request review')}`;
  const reason = status?.rejectionReason?.trim()
    ? status.rejectionReason.trim()
    : 'We were unable to approve this request based on the information provided.';

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-transparent z-0">
      <CyberBackground />
      {/* Top Bar */}
      <header className="fixed top-0 w-full z-50 flex justify-between items-center px-8 h-20 bg-card/30 backdrop-blur-xl">
        <div className="flex items-center gap-4">
          <AppLogo variant="full" alt="BlueB" className="h-10 w-auto" />
          <div className="h-6 w-px bg-border/50 mx-2 hidden sm:block"></div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-foreground">Organization Registration</h1>
        </div>
      </header>

      <div className="flex flex-1 items-center justify-center pt-20 px-4 py-12">
        <div className="relative w-full max-w-2xl rounded-3xl border border-border bg-card p-8 shadow-soft md:p-10">
          <div className="mb-5 flex justify-center">
            <AlertCircle className="h-14 w-14 text-destructive" />
          </div>

        <p className="text-center text-xs font-semibold uppercase tracking-[0.3em] text-destructive">
          Access control
        </p>
        <h1 className="mt-2 text-center font-heading text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Organization Request Rejected
        </h1>

        <div className="mt-6 rounded-2xl border border-destructive/20 bg-destructive/10 p-5 text-foreground">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-destructive">Why this happened</p>
          <p className="mt-3 text-base leading-relaxed text-foreground">
            {reason}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            If you believe we made a mistake, reply to the original request or contact us and we will review it.
          </p>
        </div>

        <div className="mt-6 rounded-2xl border border-border bg-muted/50 p-5">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">Contact support</p>
          <p className="mt-3 text-sm leading-relaxed text-foreground">
            If this rejection was an error, please email our support team with your organization name and any context that helps us review the decision.
          </p>

          <a
            href={emailHref}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
          >
            <Mail className="h-4 w-4" />
            {SUPPORT_EMAIL}
          </a>
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button
            type="button"
            variant="outline"
            className="gap-2 border-border bg-background text-foreground hover:bg-muted"
            onClick={() => void refreshStatus()}
          >
            <RefreshCcw className="h-4 w-4" />
            Refresh status
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => void keycloak.logout({ redirectUri: `${window.location.origin}/` })}
          >
            Logout
          </Button>
        </div>
      </div>
      </div>
    </div>
  );
}